// The UI callbacks' sounds (docs/platform/UiFidelity.md §3 "Audio trace"): the HUD timer's
// (`UICBInterface_ALL_SCENE::UpdateTurnTimer` 0xea6c80, the hot-seat flag of
// `UICallback_MainGame_OnIdle` 0xea84c0) and the round results' fanfare and crowd (`UpdateFanfare`
// 0xf0ea40). Plain TypeScript (node loads it in client/tests/audio-trace.mjs).

/** Plays `Worms/HUD/HotseatTick` once a second through the hot seat and `Worms/HUD/TurnTimeLowTick`
 * once a second while the turn timer flashes, on `XomGetSystemTimeSecs` (the HUD's `time`). */
export class TimerSounds {
  /** `m_bPlayHotseatTimeSound`, `m_fLastHotSeatSecond`. */
  private hotSeat = false;
  private lastHotSeat = 0;
  /** `m_bPlayTurnTimeSound`, `m_fLastTurnSecond`, `m_bTimerHasFlashed`. */
  private turnSound = false;
  private lastTurn = 0;
  private flashed = false;

  private readonly play: (path: string) => void;

  constructor(play: (path: string) => void) {
    this.play = play;
  }

  /** OnIdle's StartHotSeat: the flag on, the last second one second ago (the first tick plays
   * at once). */
  hotSeatStart(now: number): void {
    this.hotSeat = true;
    this.lastHotSeat = Math.fround(now - 1);
  }

  /** OnIdle's StartInTurn: the flag off. */
  inTurnStart(): void {
    this.hotSeat = false;
  }

  /** UpdateTurnTimer's sound part: `flash` the timer's flash condition (turn seconds in [0.1, 5]
   * in the turn, or teleporting outside the hot seat), `allowed` `BaseTurnStats::
   * AreTimerUpdatesAllowed`. */
  update(now: number, flash: boolean, allowed: boolean): void {
    if (!flash) {
      if (this.flashed) {
        this.flashed = false;
        this.turnSound = false;
      }
    } else {
      if (Math.fround(now - this.lastTurn) >= 1) {
        if (allowed) this.play('Worms/HUD/TurnTimeLowTick');
        this.lastTurn = now;
      }
      this.flashed = true;
      if (!this.turnSound) {
        this.turnSound = true;
        this.lastTurn = now;
      }
    }
    if (this.hotSeat && Math.fround(now - this.lastHotSeat) >= 1) {
      this.play('Worms/HUD/HotseatTick');
      this.lastHotSeat = now;
    }
  }
}

/** `UpdateFanfare` 0xf0ea40 (a deathmatch's results, their first OnIdle): the fanfare, then the
 * crowd. `leading`: `BaseRoundStats::GetNonLogicalLeadingTeamIndex` (-1: none), `draw`:
 * `GetGameIsADraw`, `me`: the team the crowd cheers for (a network game, or an AI leader:
 * `UserMan::MapUserToTeam` of the principal user; else the leading team). The fanfare is the
 * leading team's `FanFareGID` sound (`RGFanfare`), "Worms/Fanfares/YouLose" without one, and the
 * lobby's teams have none (crates/server lobby_spec.rs); no leading team, no fanfare. */
export function fanfareSounds(leading: number, draw: boolean, me: number): { fanfare: string | null; crowd: string } {
  const crowd = draw || leading < 0 ? 'Worms/RoundResults/SFX/CrowdDraw'
    : me === leading ? 'Worms/RoundResults/SFX/CrowdWin' : 'Worms/RoundResults/SFX/CrowdLose';
  return { fanfare: leading < 0 ? null : 'Worms/Fanfares/YouLose', crowd };
}
